import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { getCardSignals } from "../store/collection-store";
import { formatToDollarCurrencyString } from "../utils/knocking";

/**
 * Reactive price block for collection cards.
 *
 * Replaces the static `{% render 'price' %}` / `{% render 'dynamic-price-cp' %}`
 * inside product-card.liquid and variant-card.liquid.
 *
 * Attributes:
 *   data-card-id             — matches the card registered in collection-store
 *   data-label-regular       — a11y label for regular price (translated via Liquid)
 *   data-label-sale          — a11y label for sale price (translated via Liquid)
 */
@customElement("card-price")
export class CardPrice extends LitElement {
  @property({ type: String, attribute: "data-card-id" }) cardId = "";
  @property({ type: String, attribute: "data-label-regular" }) labelRegular = "Regular price";
  @property({ type: String, attribute: "data-label-sale" }) labelSale = "Sale price";

  @state() private _price: number | null = null;
  @state() private _compareAtPrice: number | null = null;

  private _disposeEffect?: () => void;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    const signals = getCardSignals(this.cardId);
    if (!signals) {
      console.error(`card-price: no signals for card "${this.cardId}"`);
      return;
    }
    this._disposeEffect = effect(() => {
      this._price = signals.price.value;
      this._compareAtPrice = signals.compareAtPrice.value;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
  }

  render() {
    const price = this._price;
    const compareAtPrice = this._compareAtPrice;
    const priceStr = price != null ? formatToDollarCurrencyString(price) : "";
    const compareFormatted =
      compareAtPrice != null ? formatToDollarCurrencyString(compareAtPrice) : null;

    return html`
      <div class="flex items-center gap-4">
        <span class="price-pp__current text-2xl font-bold text-[var(--color-secondary-500)]">${priceStr}</span>
        ${compareFormatted
          ? html`<span class="price-pp__compare text-sm line-through font-medium text-[var(--color-secondary-800)]">${compareFormatted}</span>`
          : ""}
      </div>
    `;
  }
}
